/**
 * Built-in value formatters. Use via column.format = 'number' | 'currency' | ...
 * Override per-column with column.formatOptions = { decimals, currency, locale, ... }.
 */
export const formatters = {
  text: (v) => (v == null ? "" : String(v)),

  number: (v, opts = {}) => {
    if (v == null || v === "") return "";
    const n = Number(v);
    if (Number.isNaN(n)) return v;
    return n.toLocaleString(opts.locale || undefined, {
      minimumFractionDigits: opts.decimals ?? 0,
      maximumFractionDigits: opts.decimals ?? 2
    });
  },

  currency: (v, opts = {}) => {
    if (v == null || v === "") return "";
    const n = Number(v);
    if (Number.isNaN(n)) return v;
    return new Intl.NumberFormat(opts.locale || "en-IN", {
      style: "currency",
      currency: opts.currency || "INR",
      minimumFractionDigits: opts.decimals ?? 2,
      maximumFractionDigits: opts.decimals ?? 2
    }).format(n);
  },

  date: (v) => {
    if (!v) return "";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return v;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}/${mm}/${d.getFullYear()}`;
  },

  datetime: (v) => {
    if (!v) return "";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return v;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${d.getFullYear()} ${hh}:${mi}`;
  },

  percent: (v, opts = {}) => {
    if (v == null || v === "") return "";
    const n = Number(v);
    if (Number.isNaN(n)) return v;
    const factor = opts.multiply === false ? 1 : 100;
    return (n * factor).toFixed(opts.decimals ?? 1) + "%";
  },

  boolean: (v) => (v ? "✓" : "")
};

/** Built-in aggregators for column.total. */
export const aggregators = {
  sum: (values) => values.reduce((s, v) => s + (Number(v) || 0), 0),
  avg: (values) => {
    const nums = values.map(Number).filter((n) => !Number.isNaN(n));
    return nums.length ? nums.reduce((s, n) => s + n, 0) / nums.length : 0;
  },
  count: (values) => values.filter((v) => v != null && v !== "").length,
  min: (values) => {
    const nums = values.map(Number).filter((n) => !Number.isNaN(n));
    return nums.length ? Math.min(...nums) : 0;
  },
  max: (values) => {
    const nums = values.map(Number).filter((n) => !Number.isNaN(n));
    return nums.length ? Math.max(...nums) : 0;
  }
};

/** Apply formatter / render to a value. */
export const formatValue = (value, column) => {
  if (!column) return value;
  if (column.format && formatters[column.format]) {
    return formatters[column.format](value, column.formatOptions || {});
  }
  return value ?? "";
};

/**
 * Format a TOTALS-row aggregate. Honors column.format when present; otherwise
 * any numeric aggregate is rounded to at most 2 decimals so a column that
 * forgot to declare `format` can never dump a long float into the footer.
 */
export const formatTotal = (value, column) => {
  if (column && column.format && formatters[column.format]) {
    return formatters[column.format](value, column.formatOptions || {});
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return value.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  }
  return value ?? "";
};

/** Access a cell value from a row using column.accessor or column.key. */
export const getCellValue = (row, column) => {
  if (!row || !column) return undefined;
  if (typeof column.accessor === "function") return column.accessor(row);
  return row[column.key];
};

/**
 * Stable display string used to GROUP and MATCH values in the Excel-style
 * "set" filter (checkbox list). Nulls/blanks collapse to a sentinel so they
 * can be selected like any other value. Date columns group by their rendered
 * day so the checkbox list shows human dates rather than raw timestamps.
 */
export const BLANK_TOKEN = "(Blanks)";
export const getFilterText = (row, column) => {
  const v = getCellValue(row, column);
  if (v == null || v === "") return BLANK_TOKEN;
  if (column.format === "date" || column.format === "datetime") {
    return formatters.date(v);
  }
  return String(v);
};
